import {
  ShieldCheck,
  SlidersHorizontal,
  Settings2,
  type LucideIcon,
} from "lucide-react";
import { NoxusFormEntryConfig } from "../Spotflow/ConfigDrawer/ConfigDrawer";
import { FormProvider, useFormContext, UseFormReturn } from "react-hook-form";
import { CorrectFieldWrapper } from "../Spotflow/ConfigDrawer/Configs/BaseConfig";
import { BetaFieldContext, PinFieldContext } from "./NoxusFormLabel";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { type AdvancedChipOption } from "./TiptapEditor/chipOptionsConfig";
import { Tabs, TabsContent, TabsList } from "@/components/shadcn/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import { SimpleGrid } from "../UI/SimpleGrid";
import {
  SetsFieldValues,
  VisibleIf,
  VisibleIfNot,
  VisibleIfGroup,
} from "@/types/api/spotflow/spotflow-config";
import {
  evaluateVisibilityRule,
  isConfigEntryVisible,
} from "./configVisibility";
import { ValidationResponse } from "@/generated";
import { AGENT_TAB_ORDER } from "@/components/Form/agentTabOrder";

const ADVANCED_ACCORDION = "Advanced options";
const ERROR_HANDLING_ACCORDION = "Error handling";

const ACCORDION_ICONS: Record<string, LucideIcon> = {
  Advanced: Settings2,
  "Advanced options": Settings2,
  "Output Verification": ShieldCheck,
  Safety: ShieldCheck,
};

/**
 * Returns the tab names present in a config object. Statically hidden fields
 * (`visible: false`) are skipped — they never render, so they shouldn't
 * register a tab — but dynamic visibility rules are not evaluated here.
 */
export function getConfigTabNames(
  config: Record<string, NoxusFormEntryConfig>
): string[] {
  const tabs = new Set<string>();
  Object.values(config).forEach((v) => {
    if (!v.visible) return;
    tabs.add((v as any).tab || "Configuration");
  });
  return [...tabs].sort((a, b) => {
    if (a === ERROR_HANDLING_ACCORDION) return 1;
    if (b === ERROR_HANDLING_ACCORDION) return -1;
    const order = ["Configuration", ...AGENT_TAB_ORDER];
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

export const useConfigVisibility = (
  form: UseFormReturn<any, any, any>,
  fieldPrefix?: string,
  nodeId?: string
) => {
  const formValues = form.watch();

  const resolveConfigSourceValue = useCallback(
    (configSource: string): unknown => {
      const fieldName = fieldPrefix
        ? `${fieldPrefix}.${configSource}`
        : nodeId
          ? `${nodeId}.${configSource}`
          : configSource;
      return form.watch(fieldName);
    },
    [fieldPrefix, nodeId, form, formValues] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const checkVisibilityRule = useCallback(
    (rule: VisibleIf | VisibleIfNot | VisibleIfGroup): boolean =>
      evaluateVisibilityRule(rule, resolveConfigSourceValue),
    [resolveConfigSourceValue]
  );

  const shouldRenderConfig = useCallback(
    (configItem: NoxusFormEntryConfig): boolean =>
      isConfigEntryVisible(configItem, resolveConfigSourceValue),
    [resolveConfigSourceValue]
  );

  return { checkVisibilityRule, shouldRenderConfig };
};

export const ConfigFormRendererContext = createContext<{ isEditorV2: boolean }>(
  { isEditorV2: false }
);

export const editorV2AccordionItemClass =
  "[&_[data-slot=accordion-trigger]]:rounded-none [&_[data-slot=accordion-trigger]]:focus-visible:rounded-none";
export const editorV2AccordionTriggerClass =
  "py-4 px-5 hover:no-underline rounded-none focus-visible:rounded-none";
export const editorV2AccordionContentClass = "px-5 pb-4 pt-1";

export const ConfigFormRenderer = ({
  config,
  form,
  fieldPrefix,
  nodeId,
  nodeType,
  getNodeById,
  deleteEdgesFromNode,
  updateMultiTypeDictArrayConnectors,
  nodes_fetching_config,
  getDynamicConfigsFromBackend,
  addNewDynamicOutput,
  changeDynamicOutputName,
  removeDynamicOutput,
  unselectAllNodes,
  isReadOnly,
  tabOrder,
  flattenAccordions = false,
  validation,
  externalTab,
  tab,
  onTabChange,
  excludeFields,
  chipOptions,
  showOverridableBanner = false,
  headerSlot,
  sectionLabel,
  isEditorV2 = false,
}: {
  config: Record<string, NoxusFormEntryConfig>;
  form: UseFormReturn<any, any, any>;
  fieldPrefix?: string;
  nodeId?: string;
  nodeType?: string;
  getNodeById?: (nodeId: string) => any;
  deleteEdgesFromNode?: (nodeId: string) => any;
  updateMultiTypeDictArrayConnectors?: (
    nodeId: string,
    connectors: string[],
    value: any[]
  ) => void;
  nodes_fetching_config?: string[];
  getDynamicConfigsFromBackend?: (nodeId?: string) => Promise<any>;
  addNewDynamicOutput?: (nodeId: string, connector_name: string) => any;
  changeDynamicOutputName?: (
    nodeId: string,
    key: string,
    newName: string,
    connector_name: string
  ) => any;
  removeDynamicOutput?: (
    nodeId: string,
    key: string,
    connector_name: string
  ) => any;
  unselectAllNodes?: () => any;
  isReadOnly?: boolean;
  tabOrder?: string[];
  flattenAccordions?: boolean;
  validation?: ValidationResponse;
  /** When set, renders only this tab's content without the built-in tab bar */
  externalTab?: string;
  /** The open tab, for a caller that keeps it (in the URL, say); a name the
   * config has no tab for opens the first one. */
  tab?: string;
  onTabChange?: (tab: string) => void;
  /** Field keys to exclude from rendering */
  excludeFields?: string[];
  chipOptions?: AdvancedChipOption[];
  showOverridableBanner?: boolean;
  headerSlot?: React.ReactNode;
  /** When set, wraps regular fields + accordion sections as siblings in one Accordion */
  sectionLabel?: string;
  isEditorV2?: boolean;
}) => {
  const { shouldRenderConfig } = useConfigVisibility(form, fieldPrefix, nodeId);

  const setsFieldValuesConfigs = useMemo(
    () =>
      Object.entries(config).filter(([_, configItem]) =>
        configItem.rules?.some((r: any) => r.type === "sets_field_values")
      ),
    [config]
  );

  const applyFieldValues = useCallback(
    (targetFields: string[], valuesToSet: Record<string, any>) => {
      targetFields.forEach((targetField: string) => {
        if (valuesToSet[targetField] !== undefined) {
          const targetFieldName = fieldPrefix
            ? `${fieldPrefix}.${targetField}`
            : nodeId
              ? `${nodeId}.${targetField}`
              : targetField;

          const currentTargetValue = form.getValues(targetFieldName);
          const newValue = valuesToSet[targetField];

          if (JSON.stringify(currentTargetValue) !== JSON.stringify(newValue)) {
            form.setValue(targetFieldName, newValue, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        }
      });
    },
    [form, fieldPrefix, nodeId]
  );

  useEffect(() => {
    const subscription = form?.watch((values, { name, type }) => {
      if (!name || type !== "change") return;

      const matchingConfig = setsFieldValuesConfigs.find(([fieldKey]) =>
        name.endsWith(fieldKey)
      );

      if (!matchingConfig) return;

      const [fieldKey, configItem] = matchingConfig;
      const rule = configItem.rules?.find(
        (r: { type?: string }) => r.type === "sets_field_values"
      ) as SetsFieldValues | undefined;

      if (!rule) return;

      const fullFieldName = fieldPrefix
        ? `${fieldPrefix}.${fieldKey}`
        : nodeId
          ? `${nodeId}.${fieldKey}`
          : fieldKey;

      const currentValue = form.getValues(fullFieldName);

      // A cleared field reports "" and a rule may map that key deliberately.
      const valuesToSet = rule.value_map?.[currentValue as string];
      if (!valuesToSet) return;

      const targetFields = rule.target_fields || [];

      applyFieldValues(targetFields, valuesToSet);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [form, setsFieldValuesConfigs, fieldPrefix, nodeId, applyFieldValues]);

  const { configsByTab, tabNames } = useMemo(() => {
    const grouped: Record<
      string,
      {
        regular: Record<string, NoxusFormEntryConfig>;
        accordions: Record<string, Record<string, NoxusFormEntryConfig>>;
      }
    > = {};

    Object.entries(config).forEach(([key, value]) => {
      if (excludeFields?.includes(key)) return;
      if (!shouldRenderConfig(value)) return;

      const tab = value.tab || "Configuration";

      if (!grouped[tab]) {
        grouped[tab] = { regular: {}, accordions: {} };
      }

      if (flattenAccordions) {
        // When flattening, treat all configs as regular (ignore accordion grouping)
        grouped[tab].regular[key] = value;
      } else if (value.accordion) {
        // Config belongs to an accordion
        if (!grouped[tab].accordions[value.accordion]) {
          grouped[tab].accordions[value.accordion] = {};
        }
        grouped[tab].accordions[value.accordion][key] = value;
      } else if (value.advanced) {
        // Legacy support: advanced configs go to "Advanced options" accordion
        if (!grouped[tab].accordions[ADVANCED_ACCORDION]) {
          grouped[tab].accordions[ADVANCED_ACCORDION] = {};
        }
        grouped[tab].accordions[ADVANCED_ACCORDION][key] = value;
      } else {
        // Regular config (not in accordion)
        grouped[tab].regular[key] = value;
      }
    });

    // Sort regular configs within each tab by order field
    for (const tabData of Object.values(grouped)) {
      const entries = Object.entries(tabData.regular);
      entries.sort(([, a], [, b]) => {
        const ao = (a as any).order ?? 999;
        const bo = (b as any).order ?? 999;
        return ao - bo;
      });
      tabData.regular = Object.fromEntries(entries);
    }

    // Filter out empty tabs and accordions
    const configsByTab: typeof grouped = {};
    Object.entries(grouped).forEach(([tabName, tabData]) => {
      const hasRegularConfigs = Object.keys(tabData.regular).length > 0;
      const filteredAccordions: Record<
        string,
        Record<string, NoxusFormEntryConfig>
      > = {};

      // Only include accordions that have visible configs
      Object.entries(tabData.accordions).forEach(
        ([accordionName, accordionConfigs]) => {
          if (Object.keys(accordionConfigs).length > 0) {
            filteredAccordions[accordionName] = accordionConfigs;
          }
        }
      );

      const hasAccordionConfigs = Object.keys(filteredAccordions).length > 0;

      // Only include tab if it has regular configs or non-empty accordions
      if (hasRegularConfigs || hasAccordionConfigs) {
        configsByTab[tabName] = {
          regular: tabData.regular,
          accordions: filteredAccordions,
        };
      }
    });

    const names = Object.keys(configsByTab);
    const tabNames = names.sort((a, b) => {
      if (!tabOrder || tabOrder.length === 0) return a.localeCompare(b);
      const ai = tabOrder.indexOf(a);
      const bi = tabOrder.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    return { configsByTab, tabNames };
  }, [config, shouldRenderConfig, tabOrder, flattenAccordions, excludeFields]);

  const hasMultipleTabs = tabNames.length > 1;

  const [ownTab, setOwnTab] = useState(tabNames[0]);
  const activeTab = tab ?? ownTab;
  const selectedTab = tabNames.includes(activeTab) ? activeTab : tabNames[0];
  const setActiveTab = (next: string) => {
    setOwnTab(next);
    onTabChange?.(next);
  };

  // When the caller controls the active tab externally, render that tab's
  // content only — even if just one tab has visible fields. Falling through
  // to the single-tab branch would render the same fields for every
  // externally-requested tab.
  if (externalTab) {
    const tabData = configsByTab[externalTab];
    if (!tabData) return null;
    return (
      <ConfigFormRendererContext.Provider value={{ isEditorV2: isEditorV2 }}>
        <FormProvider {...form}>
          {externalTab === "Configuration" && headerSlot}
          <TabContent
            regular={tabData.regular}
            accordions={tabData.accordions}
            fieldPrefix={fieldPrefix}
            nodeId={nodeId}
            nodeType={nodeType}
            getNodeById={getNodeById}
            deleteEdgesFromNode={deleteEdgesFromNode}
            updateMultiTypeDictArrayConnectors={
              updateMultiTypeDictArrayConnectors
            }
            nodes_fetching_config={nodes_fetching_config}
            getDynamicConfigsFromBackend={getDynamicConfigsFromBackend}
            addNewDynamicOutput={addNewDynamicOutput}
            changeDynamicOutputName={changeDynamicOutputName}
            removeDynamicOutput={removeDynamicOutput}
            unselectAllNodes={unselectAllNodes}
            isReadOnly={isReadOnly}
            flattenAccordions={flattenAccordions}
            validation={validation}
            chipOptions={chipOptions}
            showOverridableBanner={showOverridableBanner}
            sectionLabel={sectionLabel}
            isEditorV2={isEditorV2}
          />
        </FormProvider>
      </ConfigFormRendererContext.Provider>
    );
  }

  if (!hasMultipleTabs) {
    // If only one tab or no tabs, render without tab interface
    const singleTabData =
      tabNames.length === 1 ? configsByTab[tabNames[0]] : null;

    // If there are no visible configs at all, render nothing
    if (tabNames.length === 0) {
      return <></>;
    }

    return (
      <ConfigFormRendererContext.Provider value={{ isEditorV2: isEditorV2 }}>
        <FormProvider {...form}>
          <div className="w-full">
            {headerSlot}
            {singleTabData ? (
              <TabContent
                regular={singleTabData.regular}
                accordions={singleTabData.accordions}
                fieldPrefix={fieldPrefix}
                nodeId={nodeId}
                nodeType={nodeType}
                getNodeById={getNodeById}
                deleteEdgesFromNode={deleteEdgesFromNode}
                updateMultiTypeDictArrayConnectors={
                  updateMultiTypeDictArrayConnectors
                }
                nodes_fetching_config={nodes_fetching_config}
                getDynamicConfigsFromBackend={getDynamicConfigsFromBackend}
                addNewDynamicOutput={addNewDynamicOutput}
                changeDynamicOutputName={changeDynamicOutputName}
                removeDynamicOutput={removeDynamicOutput}
                unselectAllNodes={unselectAllNodes}
                isReadOnly={isReadOnly}
                flattenAccordions={flattenAccordions}
                validation={validation}
                chipOptions={chipOptions}
                showOverridableBanner={showOverridableBanner}
                sectionLabel={sectionLabel}
                isEditorV2={isEditorV2}
              />
            ) : (
              <SimpleGrid columns={12} gap={4}>
                {Object.entries(config)
                  .filter(
                    ([key, value]) =>
                      !excludeFields?.includes(key) && shouldRenderConfig(value)
                  )
                  .flatMap(([key, value]) => [
                    <ConfigFormItem
                      key={key}
                      field_key={fieldPrefix ? `${fieldPrefix}.${key}` : key}
                      config={value}
                      fieldPrefix={fieldPrefix}
                      disabled={isReadOnly}
                      validation={validation}
                      chipOptions={chipOptions}
                      showOverridableBanner={showOverridableBanner}
                    />,
                  ])}
              </SimpleGrid>
            )}
          </div>
        </FormProvider>
      </ConfigFormRendererContext.Provider>
    );
  }

  return (
    <ConfigFormRendererContext.Provider value={{ isEditorV2: isEditorV2 }}>
      <FormProvider {...form}>
        <Tabs
          value={selectedTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList
            overflow
            tabs={tabNames}
            activeTab={selectedTab}
            onSelect={setActiveTab}
            className="noxus-tab-list"
          />

          {tabNames.map((tabName) => (
            <TabsContent key={tabName} value={tabName}>
              <div className="mt-6">
                {tabName === "Configuration" && headerSlot}
                <TabContent
                  regular={configsByTab[tabName].regular}
                  accordions={configsByTab[tabName].accordions}
                  fieldPrefix={fieldPrefix}
                  nodeId={nodeId}
                  nodeType={nodeType}
                  getNodeById={getNodeById}
                  deleteEdgesFromNode={deleteEdgesFromNode}
                  updateMultiTypeDictArrayConnectors={
                    updateMultiTypeDictArrayConnectors
                  }
                  nodes_fetching_config={nodes_fetching_config}
                  getDynamicConfigsFromBackend={getDynamicConfigsFromBackend}
                  addNewDynamicOutput={addNewDynamicOutput}
                  changeDynamicOutputName={changeDynamicOutputName}
                  removeDynamicOutput={removeDynamicOutput}
                  unselectAllNodes={unselectAllNodes}
                  isReadOnly={isReadOnly}
                  flattenAccordions={flattenAccordions}
                  validation={validation}
                  showOverridableBanner={showOverridableBanner}
                  isEditorV2={isEditorV2}
                />
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </FormProvider>
    </ConfigFormRendererContext.Provider>
  );
};

export const ConfigFormItem = ({
  field_key,
  config,
  fieldPrefix,
  nodeId,
  nodeType,
  getNodeById,
  deleteEdgesFromNode,
  updateMultiTypeDictArrayConnectors,
  nodes_fetching_config,
  getDynamicConfigsFromBackend,
  addNewDynamicOutput,
  changeDynamicOutputName,
  removeDynamicOutput,
  unselectAllNodes,
  disabled,
  validation,
  chipOptions,
  showOverridableBanner,
}: {
  field_key: string;
  config: NoxusFormEntryConfig;
  fieldPrefix?: string;
  nodeId?: string;
  nodeType?: string;
  getNodeById?: (nodeId: string) => any;
  deleteEdgesFromNode?: (nodeId: string) => any;
  updateMultiTypeDictArrayConnectors?: (
    nodeId: string,
    connectors: string[],
    value: any[]
  ) => void;
  nodes_fetching_config?: string[];
  getDynamicConfigsFromBackend?: (nodeId?: string) => Promise<any>;
  addNewDynamicOutput?: (nodeId: string, connector_name: string) => any;
  changeDynamicOutputName?: (
    nodeId: string,
    key: string,
    newName: string,
    connector_name: string
  ) => any;
  removeDynamicOutput?: (
    nodeId: string,
    key: string,
    connector_name: string
  ) => any;
  unselectAllNodes?: () => any;
  disabled?: boolean;
  validation?: ValidationResponse;
  chipOptions?: AdvancedChipOption[];
  showOverridableBanner?: boolean;
}) => {
  const isOverridable =
    showOverridableBanner && (config as any).overridable_per_test_case === true;
  const isBeta = config.beta === true;

  return (
    <div
      className="group/field w-full relative empty:hidden"
      data-field-key={field_key}
      data-field-type={config.display?.type}
      style={{
        gridColumn: `span ${config.display?.col_span || 12}`,
        alignSelf: config.display?.col_align || undefined,
        justifySelf: config.display?.col_justify || undefined,
      }}
    >
      {isOverridable && (
        <span className="absolute top-0 right-0 text-xs text-muted-foreground">
          Default value, overridable per case
        </span>
      )}
      <BetaFieldContext.Provider value={isBeta}>
        <PinFieldContext.Provider value={{ nodeId, fieldKey: field_key }}>
          <GenericConfigField
            field_key={field_key}
            config={config}
            id={nodeId}
            nodeType={nodeType}
            getNodeById={getNodeById}
            deleteEdgesFromNode={deleteEdgesFromNode}
            updateMultiTypeDictArrayConnectors={
              updateMultiTypeDictArrayConnectors
            }
            nodes_fetching_config={nodes_fetching_config}
            getDynamicConfigsFromBackend={getDynamicConfigsFromBackend}
            addNewDynamicOutput={addNewDynamicOutput}
            changeDynamicOutputName={changeDynamicOutputName}
            removeDynamicOutput={removeDynamicOutput}
            unselectAllNodes={unselectAllNodes}
            disabled={disabled}
            validation={validation}
            chipOptions={chipOptions}
          />
        </PinFieldContext.Provider>
      </BetaFieldContext.Provider>
    </div>
  );
};

const noOp = (value?: any) => {};
const noOpPromise = async (value?: any) => {
  return Promise.resolve(undefined);
};

const GenericConfigField = ({
  field_key,
  config,
  id,
  nodeType,
  getNodeById,
  deleteEdgesFromNode,
  updateMultiTypeDictArrayConnectors,
  nodes_fetching_config,
  getDynamicConfigsFromBackend,
  addNewDynamicOutput,
  changeDynamicOutputName,
  removeDynamicOutput,
  unselectAllNodes,
  disabled,
  validation,
  chipOptions,
}: {
  field_key: string;
  config: NoxusFormEntryConfig;
  id?: string;
  nodeType?: string;
  getNodeById?: (nodeId: string) => any;
  deleteEdgesFromNode?: (nodeId: string) => any;
  updateMultiTypeDictArrayConnectors?: (
    nodeId: string,
    connectors: string[],
    value: any[]
  ) => void;
  nodes_fetching_config?: string[];
  getDynamicConfigsFromBackend?: (nodeId?: string) => Promise<any>;
  addNewDynamicOutput?: (nodeId: string, connector_name: string) => any;
  changeDynamicOutputName?: (
    nodeId: string,
    key: string,
    newName: string,
    connector_name: string
  ) => any;
  removeDynamicOutput?: (
    nodeId: string,
    key: string,
    connector_name: string
  ) => any;
  unselectAllNodes?: () => any;
  disabled?: boolean;
  validation?: ValidationResponse;
  chipOptions?: AdvancedChipOption[];
}) => {
  const form = useFormContext();
  const variable_type = id ? form.watch(`${id}.type`) : undefined;

  if (!config.visible) return null;

  return (
    <CorrectFieldWrapper
      id={id || ""}
      node_type={nodeType || ""}
      field_key={field_key}
      config={config}
      variable_type={variable_type}
      getNodeById={getNodeById || noOp}
      deleteEdgesFromNode={deleteEdgesFromNode || noOp}
      updateMultiTypeDictArrayConnectors={
        updateMultiTypeDictArrayConnectors || noOp
      }
      nodes_fetching_config={nodes_fetching_config || []}
      getDynamicConfigsFromBackend={
        getDynamicConfigsFromBackend || (() => noOpPromise())
      }
      addNewDynamicOutput={addNewDynamicOutput || noOp}
      changeDynamicOutputName={changeDynamicOutputName || noOp}
      removeDynamicOutput={removeDynamicOutput || noOp}
      unselectAllNodes={unselectAllNodes || noOp}
      hasPrecheckedVisibility={true}
      isReadOnly={disabled}
      validation={validation}
      chipOptions={chipOptions}
    />
  );
};

const TabContent = ({
  regular,
  accordions,
  fieldPrefix,
  nodeId,
  nodeType,
  getNodeById,
  deleteEdgesFromNode,
  updateMultiTypeDictArrayConnectors,
  nodes_fetching_config,
  getDynamicConfigsFromBackend,
  addNewDynamicOutput,
  changeDynamicOutputName,
  removeDynamicOutput,
  unselectAllNodes,
  isReadOnly,
  flattenAccordions,
  validation,
  chipOptions,
  showOverridableBanner,
  sectionLabel,
  isEditorV2 = false,
}: {
  regular: Record<string, NoxusFormEntryConfig>;
  accordions: Record<string, Record<string, NoxusFormEntryConfig>>;
  fieldPrefix?: string;
  nodeId?: string;
  nodeType?: string;
  getNodeById?: (nodeId: string) => any;
  deleteEdgesFromNode?: (nodeId: string) => any;
  updateMultiTypeDictArrayConnectors?: (
    nodeId: string,
    connectors: string[],
    value: any[]
  ) => void;
  nodes_fetching_config?: string[];
  getDynamicConfigsFromBackend?: (nodeId?: string) => Promise<any>;
  addNewDynamicOutput?: (nodeId: string, connector_name: string) => any;
  changeDynamicOutputName?: (
    nodeId: string,
    key: string,
    newName: string,
    connector_name: string
  ) => any;
  removeDynamicOutput?: (
    nodeId: string,
    key: string,
    connector_name: string
  ) => any;
  unselectAllNodes?: () => any;
  isReadOnly?: boolean;
  flattenAccordions?: boolean;
  validation?: ValidationResponse;
  chipOptions?: AdvancedChipOption[];
  showOverridableBanner?: boolean;
  sectionLabel?: string;
  isEditorV2?: boolean;
}) => {
  const accordionNames = Object.keys(accordions).sort();
  const hasRegular = Object.keys(regular).length > 0;
  const editorAccordionTriggerClass = isEditorV2
    ? editorV2AccordionTriggerClass
    : "text-base font-semibold py-2 my-2 px-0 hover:no-underline";
  const editorAccordionContentClass = isEditorV2 ? "px-5 pb-4 pt-1" : "p-1";

  const renderConfigFields = (fields: Record<string, NoxusFormEntryConfig>) =>
    Object.entries(fields).flatMap(([key, value]) => [
      <ConfigFormItem
        key={key}
        field_key={fieldPrefix ? `${fieldPrefix}.${key}` : key}
        config={value}
        fieldPrefix={fieldPrefix}
        nodeId={nodeId}
        nodeType={nodeType}
        getNodeById={getNodeById}
        deleteEdgesFromNode={deleteEdgesFromNode}
        updateMultiTypeDictArrayConnectors={updateMultiTypeDictArrayConnectors}
        nodes_fetching_config={nodes_fetching_config}
        getDynamicConfigsFromBackend={getDynamicConfigsFromBackend}
        addNewDynamicOutput={addNewDynamicOutput}
        changeDynamicOutputName={changeDynamicOutputName}
        removeDynamicOutput={removeDynamicOutput}
        unselectAllNodes={unselectAllNodes}
        disabled={isReadOnly}
        validation={validation}
        chipOptions={chipOptions}
        showOverridableBanner={showOverridableBanner}
      />,
    ]);

  const regularFieldsGrid = (
    <SimpleGrid columns={12} gap={1.5}>
      {renderConfigFields(regular)}
    </SimpleGrid>
  );

  if (sectionLabel) {
    return (
      <div className="w-full">
        <Accordion
          type="multiple"
          defaultValue={[sectionLabel]}
          className="w-full"
        >
          {hasRegular && (
            <AccordionItem
              value={sectionLabel}
              lastBorder={accordionNames.length === 0}
              className={isEditorV2 ? editorV2AccordionItemClass : undefined}
            >
              <AccordionTrigger className={editorAccordionTriggerClass}>
                <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <SlidersHorizontal
                    size={14}
                    strokeWidth={2}
                    className="shrink-0"
                  />
                  {sectionLabel}
                </span>
              </AccordionTrigger>
              <AccordionContent className={editorAccordionContentClass}>
                {regularFieldsGrid}
              </AccordionContent>
            </AccordionItem>
          )}
          {accordionNames.map((accordionName, idx) => {
            const Icon = ACCORDION_ICONS[accordionName] ?? SlidersHorizontal;
            const isLast = idx === accordionNames.length - 1;
            return (
              <AccordionItem
                key={accordionName}
                value={accordionName}
                lastBorder={isLast}
                className={isEditorV2 ? editorV2AccordionItemClass : undefined}
              >
                <AccordionTrigger className={editorAccordionTriggerClass}>
                  <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <Icon size={14} strokeWidth={2} className="shrink-0" />
                    {accordionName}
                  </span>
                </AccordionTrigger>
                <AccordionContent className={editorAccordionContentClass}>
                  <SimpleGrid columns={12} gap={1.5}>
                    {renderConfigFields(accordions[accordionName])}
                  </SimpleGrid>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full items-stretch gap-6">
      {hasRegular &&
        (isEditorV2 ? (
          <div className="px-5">{regularFieldsGrid}</div>
        ) : (
          regularFieldsGrid
        ))}

      {/* Render accordion configs */}
      {accordionNames.length > 0 && (
        <div className="w-full">
          <Accordion type="multiple" className="w-full">
            {accordionNames.map((accordionName) => (
              <AccordionItem
                key={accordionName}
                value={accordionName}
                firstBorder={isEditorV2}
                lastBorder
                className={isEditorV2 ? editorV2AccordionItemClass : undefined}
              >
                <AccordionTrigger className={editorAccordionTriggerClass}>
                  {isEditorV2
                    ? (() => {
                        const Icon =
                          ACCORDION_ICONS[accordionName] ?? SlidersHorizontal;
                        return (
                          <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                            <Icon
                              size={14}
                              strokeWidth={2}
                              className="shrink-0"
                            />
                            {accordionName}
                          </span>
                        );
                      })()
                    : accordionName}
                </AccordionTrigger>
                <AccordionContent className={editorAccordionContentClass}>
                  <SimpleGrid columns={12} gap={1.5}>
                    {renderConfigFields(accordions[accordionName])}
                  </SimpleGrid>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
};
